import React, { useEffect, useState, useCallback } from 'react';
import {
  Content,
  ContentHeader,
} from '@backstage/core-components';
import {
  ConfigApi,
  configApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import PauseCircleFilledIcon from '@material-ui/icons/PauseCircleFilled';
import WarningIcon from '@material-ui/icons/Warning';
import MergeTypeIcon from '@material-ui/icons/MergeType';
import BuildIcon from '@material-ui/icons/Build';

/* ─── Types ─── */

interface Runner {
  id: number;
  description: string;
  active: boolean;
  paused: boolean;
  is_shared: boolean;
  runner_type: string;
  online: boolean;
  status: string;
  ip_address?: string;
  tag_list: string[];
  version?: string;
  architecture?: string;
  platform?: string;
  contacted_at?: string;
  projects?: { id: number; name: string; path_with_namespace: string }[];
}

interface Pipeline {
  id: number;
  iid: number;
  status: string;
  ref: string;
  sha: string;
  created_at: string;
  updated_at: string;
  duration?: number;
  web_url: string;
  source?: string;
}

export interface Project {
  id: number;
  name: string;
  path_with_namespace: string;
  web_url: string;
  last_activity_at: string;
  namespace?: { name: string; path: string };
  topics?: string[];
  archived?: boolean;
  /** GitLab 지연 삭제 예약 표시 — CE 18.x 는 두 이름 중 하나로 내려준다 */
  marked_for_deletion_at?: string | null;
  marked_for_deletion_on?: string | null;
  _latestPipeline?: Pipeline;
  _openMRCount?: number;
}

interface RunnerJob {
  id: number;
  status: string;
  name: string;
  ref: string;
  created_at: string;
  pipeline: { id: number };
  project: { path_with_namespace: string };
}

/* ─── Styles ─── */

const useStyles = makeStyles(theme => ({
  summaryCard: {
    height: '100%',
    minHeight: 120,
  },
  summaryValue: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1.2,
  },
  summaryLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    marginTop: theme.spacing(0.5),
  },
  statusOnline: { color: '#4caf50' },
  statusOffline: { color: '#f44336' },
  statusPaused: { color: '#ff9800' },
  chipSuccess: { backgroundColor: '#4caf50', color: '#fff', fontWeight: 600, fontSize: '0.7rem' },
  chipFailed: { backgroundColor: '#f44336', color: '#fff', fontWeight: 600, fontSize: '0.7rem' },
  chipRunning: { backgroundColor: '#2196f3', color: '#fff', fontWeight: 600, fontSize: '0.7rem' },
  chipPending: { backgroundColor: '#ff9800', color: '#fff', fontWeight: 600, fontSize: '0.7rem' },
  chipCanceled: { backgroundColor: '#9e9e9e', color: '#fff', fontWeight: 600, fontSize: '0.7rem' },
  chipSkipped: { backgroundColor: '#bdbdbd', color: '#424242', fontWeight: 600, fontSize: '0.7rem' },
  chipManual: { backgroundColor: '#7b1fa2', color: '#fff', fontWeight: 600, fontSize: '0.7rem' },
  chipDefault: { fontWeight: 600, fontSize: '0.7rem' },
  tagChip: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    height: 22,
    fontSize: '0.7rem',
  },
  expandRow: {
    backgroundColor: theme.palette.background.default,
  },
  mono: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
  },
  refreshBtn: {
    marginLeft: theme.spacing(1),
  },
  filterControl: {
    minWidth: 120,
    marginRight: theme.spacing(2),
  },
  tableLink: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
}));

/* ─── Arcus 스코프 필터 (design/21) ─── */

/**
 * 스키마 기본값. GitLab 서버가 타 팀 공용이라 필터 없이는 Arcus 것이 한 건도 안 보인다
 * (design/21 §1-1). 환경 종속값이 아니라 전 환경 공통 기본값이며, 운영 SoT 는
 * `charts/arcus/values-{dev,prd}.yaml` 의 `arcus.gitlabDashboard` 다.
 */
export const DEFAULT_SCOPE_TOPICS = [
  'arcus-managed',
  'arcus-managed-prd',
  'arcus-apigateway',
  'arcus-platform',
];
export const DEFAULT_ENRICH_LIMIT = 50;
/** GitLab `/projects` 한 페이지 상한 */
export const PROJECT_PAGE_SIZE = 100;
/** GitLab 러너 조회 한 페이지 상한 */
export const RUNNER_PAGE_SIZE = 100;

export interface GitlabScopeConfig {
  /** 표시 대상 topic 합집합 */
  topics: string[];
  /** topic 부여를 잊은 repo 구제용 폴백 경로 목록 — 비어 있는 것이 정상 (design/21 §4-1) */
  platformProjects: string[];
  /**
   * 러너를 조회할 GitLab 그룹(경로 또는 수치 id). design/21 §11-3.
   * 비-admin 토큰은 `/runners/all` 이 403 이고, 전역 `/runners` 는 본인이 멤버인
   * `project_type` 러너만 준다 = Arcus 인클러스터 러너(`instance_type`)가 통째로
   * 빠지고 타 팀 로컬 러너만 남는 역전이 난다. 그룹 러너 API 는 같은 토큰으로
   * Arcus 러너를 정확히 준다.
   * ★ 그룹명은 설치처마다 다르므로 코드 기본값을 두지 않는다(하드코딩 금지).
   */
  runnerGroups: string[];
  excludeDeletionScheduled: boolean;
  enrichLimit: number;
}

export const readScopeConfig = (
  configApi: ConfigApi,
): { scope: GitlabScopeConfig; configError?: string } => {
  const fallback: GitlabScopeConfig = {
    topics: DEFAULT_SCOPE_TOPICS,
    platformProjects: [],
    runnerGroups: [],
    excludeDeletionScheduled: true,
    enrichLimit: DEFAULT_ENRICH_LIMIT,
  };
  try {
    const cfg = configApi.getOptionalConfig('arcus.gitlabDashboard');
    return {
      scope: {
        topics: cfg?.getOptionalStringArray('topics') ?? fallback.topics,
        platformProjects: cfg?.getOptionalStringArray('platformProjects') ?? [],
        runnerGroups: cfg?.getOptionalStringArray('runnerGroups') ?? [],
        excludeDeletionScheduled:
          cfg?.getOptionalBoolean('excludeDeletionScheduled') ?? true,
        enrichLimit:
          cfg?.getOptionalNumber('enrichLimit') ?? DEFAULT_ENRICH_LIMIT,
      },
    };
  } catch (e: any) {
    // values 에 타입이 잘못 들어와도 페이지 전체를 죽이지 않는다. 대신 기본값으로
    // 내려앉은 사실을 화면에 남긴다 — 조용히 기본값을 쓰면 설정이 죽은 줄 모른다.
    return {
      scope: fallback,
      configError: `arcus.gitlabDashboard 설정을 읽지 못해 기본값으로 동작합니다: ${
        e?.message || 'unknown error'
      }`,
    };
  }
};

/**
 * 디커미션 예약 repo 판정. API 필드가 정본이고, 리네임 규칙
 * (`<name>-deletion_scheduled-<id>`)은 필드가 안 내려오는 경우의 보조다.
 */
export const isDeletionScheduled = (p: Project): boolean =>
  Boolean(p.marked_for_deletion_at || p.marked_for_deletion_on) ||
  p.path_with_namespace.includes('-deletion_scheduled-');

/** topic 별 조회 결과 + 폴백 조회 결과를 id 기준 합집합으로 합친다. */
export const mergeScopedProjects = (
  lists: Project[][],
  opts: { excludeDeletionScheduled: boolean },
): Project[] => {
  const byId = new Map<number, Project>();
  for (const list of lists) {
    for (const p of list) {
      if (!p || typeof p.id !== 'number') continue;
      if (opts.excludeDeletionScheduled && isDeletionScheduled(p)) continue;
      if (!byId.has(p.id)) byId.set(p.id, p);
    }
  }
  return [...byId.values()].sort(
    (a, b) =>
      new Date(b.last_activity_at).getTime() -
      new Date(a.last_activity_at).getTime(),
  );
};

/** 그룹 필터 선택지 = 스코프 결과의 최상위 네임스페이스 (별도 `/groups` 순회 불필요) */
export const deriveGroupPaths = (projects: Project[]): string[] =>
  [
    ...new Set(
      projects
        .map(p => p.path_with_namespace.split('/')[0])
        .filter(Boolean),
    ),
  ].sort();

/* ─── Helpers ─── */

const pipelineChipClass = (status: string, classes: ReturnType<typeof useStyles>) => {
  switch (status) {
    case 'success': return classes.chipSuccess;
    case 'failed': return classes.chipFailed;
    case 'running': return classes.chipRunning;
    case 'pending': case 'waiting_for_resource': return classes.chipPending;
    case 'canceled': return classes.chipCanceled;
    case 'skipped': return classes.chipSkipped;
    case 'manual': return classes.chipManual;
    default: return classes.chipDefault;
  }
};

const timeAgo = (dateStr: string) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const fmtDuration = (sec?: number) => {
  if (!sec) return '-';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

/* ─── Component ─── */

export const GitlabPage = () => {
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);

  const gitlabCfg = configApi.getOptionalConfigArray('integrations.gitlab')?.[0];
  const hostOrBase =
    gitlabCfg?.getOptionalString('baseUrl') ??
    gitlabCfg?.getOptionalString('url') ??
    gitlabCfg?.getOptionalString('host');
  const gitlabBaseUrl =
    hostOrBase?.startsWith('http') ? hostOrBase : hostOrBase ? `https://${hostOrBase}` : '';

  // Arcus 스코프 필터 설정 (design/21 §4). configApi 는 런타임 내내 불변이라 1회 판독.
  const [{ scope, configError }] = useState(() => readScopeConfig(configApi));

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 축소·절단·부분 실패를 화면에 남긴다 — 조용한 축소는 가짜그린이다.
  // ★ 러너 축과 스코프 축을 나눠 둔다. 한 덩어리면 뒤 단계에서 예외가 났을 때
  //   앞 단계 경고가 통째로 유실되거나, 반대로 직전 사이클 문구가 눌러앉는다.
  const [runnerNotices, setRunnerNotices] = useState<string[]>([]);
  const [scopeNotices, setScopeNotices] = useState<string[]>([]);
  const notices = [...runnerNotices, ...scopeNotices];
  /** topic 축별 건수 — 멤버십이 조용히 줄면 여기서 먼저 드러난다. */
  const [topicBreakdown, setTopicBreakdown] = useState<string[]>([]);

  // Data
  const [runners, setRunners] = useState<Runner[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [recentPipelines, setRecentPipelines] = useState<(Pipeline & { project_path: string })[]>([]);
  const [expandedRunner, setExpandedRunner] = useState<number | null>(null);
  const [runnerJobs, setRunnerJobs] = useState<Record<number, RunnerJob[]>>({});
  /** 러너별 job 조회 실패 사유 — 빈 목록과 조회 실패를 구분한다. */
  const [runnerJobsError, setRunnerJobsError] = useState<
    Record<number, string | null>
  >({});
  const [groupFilter, setGroupFilter] = useState('all');
  const [groups, setGroups] = useState<string[]>([]);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const gitlabFetch = useCallback(async (path: string) => {
    const resp = await fetchApi.fetch(`/api/proxy/gitlab/api/v4${path}`);
    if (!resp.ok) {
      if (resp.status === 403 || resp.status === 401) {
        throw new Error(`GitLab API ${resp.status}: Admin 토큰 권한이 필요합니다.`);
      }
      throw new Error(`GitLab API ${resp.status}: ${resp.statusText}`);
    }
    return resp.json();
  }, [fetchApi]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Runners — admin 이면 전역, 아니면 그룹 스코프 (design/21 §11-3)
      const runnerMsgs: string[] = [];
      let runnerList: Runner[] = [];
      try {
        const res = await gitlabFetch('/runners/all?per_page=100');
        runnerList = Array.isArray(res) ? res : [];
      } catch (e: any) {
        if (e.message?.includes('403') || e.message?.includes('401')) {
          // 비-admin 폴백. ★ 전역 `/runners` 를 쓰면 안 된다 — 본인이 멤버인
          // `project_type` 러너만 돌려주므로 Arcus 인클러스터 러너가 전부
          // 빠지고 타 팀 로컬 러너만 남는다(정확히 뒤집힌 결과).
          if (scope.runnerGroups.length === 0) {
            runnerMsgs.push(
              'runnerGroups 가 설정되지 않아 러너를 조회할 수 없습니다 — arcus.gitlabDashboard.runnerGroups 를 지정하세요.',
            );
          }
          const byId = new Map<number, Runner>();
          for (const group of scope.runnerGroups) {
            try {
              const res = await gitlabFetch(
                `/groups/${encodeURIComponent(group)}/runners?per_page=100`,
              );
              const list: Runner[] = Array.isArray(res) ? res : [];
              if (list.length >= RUNNER_PAGE_SIZE) {
                runnerMsgs.push(
                  `그룹 '${group}' 러너 조회가 페이지 상한 ${RUNNER_PAGE_SIZE}건에 도달했습니다 — 일부가 누락됐을 수 있습니다.`,
                );
              }
              for (const r of list) {
                if (r && typeof r.id === 'number' && !byId.has(r.id)) {
                  byId.set(r.id, r);
                }
              }
            } catch (ge: any) {
              runnerMsgs.push(
                `그룹 '${group}' 러너 조회 실패: ${ge?.message || 'unknown error'}`,
              );
            }
          }
          runnerList = [...byId.values()];
        } else {
          // ★ 403/401 이 아닌 실패(5xx·네트워크·프록시 다운)를 삼키면
          //   'Runners Online 0' 이 정상처럼 보인다. 폴백 대상이 아니므로
          //   조회는 포기하되 이유는 반드시 남긴다.
          runnerMsgs.push(
            `러너 조회 실패: ${e?.message || 'unknown error'}`,
          );
        }
      }
      if (runnerList.length === 0 && runnerMsgs.length === 0) {
        runnerMsgs.push(
          `러너 조회 결과가 0건입니다 — 그룹(${
            scope.runnerGroups.join(', ') || '미설정'
          })과 포털 토큰 가시 범위를 확인하세요.`,
        );
      }
      setRunners(runnerList);
      // 러너 축은 여기서 확정 — 아래 스코프 처리가 실패해도 유실되지 않는다.
      setRunnerNotices(runnerMsgs);

      // 2. Arcus 스코프 프로젝트 = topic 합집합 ∪ platformProjects 폴백 − 삭제예약
      //    서버 측 topic 필터라 그룹 순회가 없다 (design/21 §3-1·§4-1).
      const scopeMsgs: string[] = configError ? [configError] : [];

      const perTopicCounts: string[] = [];
      const topicLists = await Promise.all(
        scope.topics.map(async topic => {
          try {
            const res = await gitlabFetch(
              // ★ archived=false — 디커미션 카드의 Archive 경로로 넘어간 repo 는
              //   topic 이 남아 있어도 표시 대상이 아니다. 카탈로그 디스커버리가
              //   archive 시점부터 제외하는 것과 축을 맞춘다.
              `/projects?topic=${encodeURIComponent(topic)}&membership=true` +
                `&archived=false&per_page=${PROJECT_PAGE_SIZE}&order_by=last_activity_at`,
            );
            const list: Project[] = Array.isArray(res) ? res : [];
            if (list.length >= PROJECT_PAGE_SIZE) {
              scopeMsgs.push(
                `topic '${topic}' 조회가 페이지 상한 ${PROJECT_PAGE_SIZE}건에 도달했습니다 — 일부가 누락됐을 수 있습니다.`,
              );
            }
            // 토큰 멤버십이 조용히 줄면 200 + 짧은 배열로 돌아온다(전체가 0이 아니면
            // 위 0건 경고에도 안 걸린다). 축별 건수를 늘 노출해 축소를 눈에 보이게 한다.
            perTopicCounts.push(`${topic} ${list.length}`);
            return list;
          } catch (e: any) {
            // 조용히 빈 배열로 넘기지 않는다 — 무성 실패가 가짜그린의 근본이다.
            scopeMsgs.push(
              `topic '${topic}' 조회 실패: ${e?.message || 'unknown error'}`,
            );
            perTopicCounts.push(`${topic} 실패`);
            return [] as Project[];
          }
        }),
      );
      setTopicBreakdown(perTopicCounts);

      // 폴백 = topic 부여를 잊은 repo 를 재빌드 없이 구제하는 자리. 평시엔 빈 목록.
      const fallbackLists = await Promise.all(
        scope.platformProjects.map(async path => {
          try {
            const proj: Project = await gitlabFetch(
              `/projects/${encodeURIComponent(path)}`,
            );
            return proj?.id ? [proj] : [];
          } catch (e: any) {
            scopeMsgs.push(
              `platformProjects '${path}' 조회 실패: ${e?.message || 'unknown error'}`,
            );
            return [] as Project[];
          }
        }),
      );

      const scopedProjects = mergeScopedProjects(
        [...topicLists, ...fallbackLists],
        { excludeDeletionScheduled: scope.excludeDeletionScheduled },
      );

      if (scopedProjects.length === 0) {
        scopeMsgs.push(
          `Arcus 스코프 결과가 0건입니다 — topic 설정(${
            scope.topics.join(', ') || '없음'
          })과 포털 토큰 가시성을 확인하세요.`,
        );
      }

      setGroups(deriveGroupPaths(scopedProjects));

      // 3. Latest pipeline + open MRs per project (parallel, limited)
      if (scopedProjects.length > scope.enrichLimit) {
        scopeMsgs.push(
          `파이프라인·MR 보강이 상위 ${scope.enrichLimit}건에서 잘렸습니다 (스코프 ${scopedProjects.length}건). 나머지 행의 값은 비어 있습니다.`,
        );
      }
      let enrichFailures = 0;
      const enriched = await Promise.all(
        scopedProjects.slice(0, scope.enrichLimit).map(async proj => {
          try {
            const [pipelines, mrs] = await Promise.all([
              gitlabFetch(`/projects/${proj.id}/pipelines?per_page=1`),
              // ★ per_page=1 이면 열린 MR 이 몇 건이든 길이가 늘 1 이하가 된다
              //   = 요약 카드가 'MR 총계'가 아니라 'MR 있는 프로젝트 수'를 세게 된다.
              gitlabFetch(
                `/projects/${proj.id}/merge_requests?state=opened&per_page=${PROJECT_PAGE_SIZE}`,
              ),
            ]);
            return {
              ...proj,
              _latestPipeline: pipelines[0] || undefined,
              _openMRCount: Array.isArray(mrs) ? mrs.length : 0,
            } as Project;
          } catch {
            // 개별 실패는 화면을 죽이지 않되, 건수는 반드시 남긴다 —
            // 전량 실패해도 조용하면 'Failed 0 / MR 0' 완전한 그린으로 보인다.
            enrichFailures += 1;
            return proj;
          }
        }),
      );
      if (enrichFailures > 0) {
        scopeMsgs.push(
          `파이프라인·MR 보강이 ${enrichFailures}건 실패했습니다 — 해당 행과 요약 카드 수치가 실제보다 낮습니다.`,
        );
      }
      setScopeNotices(scopeMsgs);
      // 보강 상한을 넘은 나머지도 목록에는 남긴다 — 잘린 것은 값이지 행이 아니다.
      setProjects([...enriched, ...scopedProjects.slice(scope.enrichLimit)]);

      // 4. Recent failed + recent pipelines (across all projects)
      const allPipelines: (Pipeline & { project_path: string })[] = [];
      for (const proj of enriched) {
        if (proj._latestPipeline) {
          allPipelines.push({
            ...proj._latestPipeline,
            project_path: proj.path_with_namespace,
          });
        }
      }
      allPipelines.sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      );
      setRecentPipelines(allPipelines);

      setLastRefresh(new Date());
    } catch (e: any) {
      setError(e.message || 'Failed to load GitLab data');
      // ★ 스코프 축 경고를 비운다 — 안 비우면 직전 사이클 문구가 눌러앉아
      //   60초마다 이미 해소된 문제를 계속 알린다. 러너 축은 이미 확정분.
      setScopeNotices([]);
    } finally {
      setLoading(false);
    }
  }, [gitlabFetch, scope, configError]);

  useEffect(() => {
    loadData();
    const iv = setInterval(loadData, 60000);
    return () => clearInterval(iv);
  }, [loadData]);

  const loadRunnerJobs = useCallback(
    async (runnerId: number) => {
      if (runnerJobs[runnerId]) return;
      try {
        const jobs: RunnerJob[] = await gitlabFetch(`/runners/${runnerId}/jobs?per_page=10`);
        setRunnerJobs(prev => ({ ...prev, [runnerId]: jobs }));
        setRunnerJobsError(prev => ({ ...prev, [runnerId]: null }));
      } catch (e: any) {
        // ★ 실패를 빈 배열로 저장하면 화면이 "No recent jobs" = 정상으로 보인다.
        //   러너 행이 이제 실제로 보이므로 이 경로에 사람이 도달한다
        //   (instance 러너의 jobs 는 비-admin 에게 403 인 축이 있다).
        setRunnerJobs(prev => ({ ...prev, [runnerId]: [] }));
        setRunnerJobsError(prev => ({
          ...prev,
          [runnerId]: e?.message || 'unknown error',
        }));
      }
    },
    [gitlabFetch, runnerJobs],
  );

  const handleExpandRunner = (runnerId: number) => {
    if (expandedRunner === runnerId) {
      setExpandedRunner(null);
    } else {
      setExpandedRunner(runnerId);
      loadRunnerJobs(runnerId);
    }
  };

  // Computed
  const onlineCount = runners.filter(r => r.online && !r.paused).length;
  const offlineCount = runners.filter(r => !r.online && !r.paused).length;
  const pausedCount = runners.filter(r => r.paused).length;

  const failedPipelines = recentPipelines.filter(p => p.status === 'failed');
  const successPipelines = recentPipelines.filter(p => p.status === 'success');
  const runningPipelines = recentPipelines.filter(
    p => p.status === 'running' || p.status === 'pending',
  );

  const filteredProjects =
    groupFilter === 'all'
      ? projects
      : projects.filter(p => p.path_with_namespace.startsWith(`${groupFilter}/`));

  /* ─── Render ─── */

  const renderOverview = () => (
    <Grid container spacing={3}>
      {/* Summary Cards */}
      <Grid item xs={12} md={3}>
        <Card className={classes.summaryCard}>
          <CardContent>
            <Typography className={classes.summaryLabel}>Runners Online</Typography>
            <Typography className={`${classes.summaryValue} ${classes.statusOnline}`}>
              {onlineCount}
            </Typography>
            {offlineCount > 0 && (
              <Typography variant="caption" className={classes.statusOffline}>
                {offlineCount} offline
              </Typography>
            )}
            {pausedCount > 0 && (
              <Typography variant="caption" className={classes.statusPaused}>
                {' '}{pausedCount} paused
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={3}>
        <Card className={classes.summaryCard}>
          <CardContent>
            <Typography className={classes.summaryLabel}>Arcus Projects</Typography>
            <Typography className={classes.summaryValue} data-testid="gitlab-project-count">
              {projects.length}
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              data-testid="gitlab-topic-breakdown"
            >
              {groups.length}개 그룹
              {topicBreakdown.length > 0 && ` · ${topicBreakdown.join(' / ')}`}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={3}>
        <Card className={classes.summaryCard}>
          <CardContent>
            <Typography className={classes.summaryLabel}>Pipelines</Typography>
            <Box display="flex" alignItems="baseline" style={{ gap: 8 }}>
              <Tooltip title="Success">
                <Chip label={successPipelines.length} size="small" className={classes.chipSuccess} />
              </Tooltip>
              <Tooltip title="Failed">
                <Chip label={failedPipelines.length} size="small" className={classes.chipFailed} />
              </Tooltip>
              <Tooltip title="Running">
                <Chip label={runningPipelines.length} size="small" className={classes.chipRunning} />
              </Tooltip>
            </Box>
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
              latest per project
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={3}>
        <Card className={classes.summaryCard}>
          <CardContent>
            <Typography className={classes.summaryLabel}>Open MRs</Typography>
            <Typography className={classes.summaryValue}>
              <MergeTypeIcon style={{ fontSize: '1.5rem', verticalAlign: 'middle', marginRight: 4 }} />
              {projects.reduce((sum, p) => sum + (p._openMRCount || 0), 0)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Failed Pipelines */}
      {failedPipelines.length > 0 && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                <ErrorIcon style={{ fontSize: 16, verticalAlign: 'middle', color: '#f44336', marginRight: 4 }} />
                Failed Pipelines
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Project</TableCell>
                      <TableCell>Branch</TableCell>
                      <TableCell>Pipeline</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>When</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {failedPipelines.slice(0, 10).map(p => (
                      <TableRow key={p.id}>
                        <TableCell>
                          <a href={`${gitlabBaseUrl}/${p.project_path}`} target="_blank" rel="noopener noreferrer" className={classes.tableLink}>
                            {p.project_path}
                          </a>
                        </TableCell>
                        <TableCell className={classes.mono}>{p.ref}</TableCell>
                        <TableCell>
                          <a href={p.web_url} target="_blank" rel="noopener noreferrer" className={classes.tableLink}>
                            #{p.iid || p.id}
                          </a>
                        </TableCell>
                        <TableCell>{fmtDuration(p.duration)}</TableCell>
                        <TableCell>{timeAgo(p.updated_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Recent Success Pipelines */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
              <CheckCircleIcon style={{ fontSize: 16, verticalAlign: 'middle', color: '#4caf50', marginRight: 4 }} />
              Recent Pipelines (Top 10)
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Project</TableCell>
                    <TableCell>Branch</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Pipeline</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>When</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentPipelines.slice(0, 10).map(p => (
                    <TableRow key={p.id}>
                      <TableCell>
                        <a href={`${gitlabBaseUrl}/${p.project_path}`} target="_blank" rel="noopener noreferrer" className={classes.tableLink}>
                          {p.project_path}
                        </a>
                      </TableCell>
                      <TableCell className={classes.mono}>{p.ref}</TableCell>
                      <TableCell>
                        <Chip label={p.status} size="small" className={pipelineChipClass(p.status, classes)} />
                      </TableCell>
                      <TableCell>
                        <a href={p.web_url} target="_blank" rel="noopener noreferrer" className={classes.tableLink}>
                          #{p.iid || p.id}
                        </a>
                      </TableCell>
                      <TableCell>{fmtDuration(p.duration)}</TableCell>
                      <TableCell>{timeAgo(p.updated_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderRunners = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 12 }}>
              <BuildIcon style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />
              GitLab Runners ({runners.length})
            </Typography>
            {runners.length === 0 ? (
              <Typography color="textSecondary">
                러너가 0건입니다. 위 경고 카드에 사유가 표시되며, 경고가 없다면
                조회 대상 그룹(<code>arcus.gitlabDashboard.runnerGroups</code>)에
                등록된 러너가 실제로 없는 것입니다.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={40} />
                      <TableCell>Runner</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Tags</TableCell>
                      <TableCell>Version</TableCell>
                      <TableCell>Last Contact</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {runners.map(runner => (
                      <React.Fragment key={runner.id}>
                        <TableRow
                          hover
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleExpandRunner(runner.id)}
                        >
                          <TableCell>
                            <IconButton size="small">
                              {expandedRunner === runner.id ? (
                                <ExpandLessIcon fontSize="small" />
                              ) : (
                                <ExpandMoreIcon fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                              {runner.description || `Runner #${runner.id}`}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              ID: {runner.id}
                              {runner.ip_address && ` | IP: ${runner.ip_address}`}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {runner.paused ? (
                              <Chip
                                icon={<PauseCircleFilledIcon style={{ fontSize: 14 }} />}
                                label="Paused"
                                size="small"
                                className={classes.chipPending}
                              />
                            ) : runner.online ? (
                              <Chip
                                icon={<FiberManualRecordIcon style={{ fontSize: 10 }} />}
                                label="Online"
                                size="small"
                                className={classes.chipSuccess}
                              />
                            ) : (
                              <Chip
                                icon={<FiberManualRecordIcon style={{ fontSize: 10 }} />}
                                label="Offline"
                                size="small"
                                className={classes.chipFailed}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={runner.is_shared ? 'shared' : runner.runner_type || 'project'}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {(runner.tag_list || []).map(tag => (
                              <Chip key={tag} label={tag} size="small" className={classes.tagChip} variant="outlined" />
                            ))}
                          </TableCell>
                          <TableCell className={classes.mono}>
                            {runner.version || '-'}
                          </TableCell>
                          <TableCell>
                            {runner.contacted_at ? timeAgo(runner.contacted_at) : '-'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={7} style={{ padding: 0, borderBottom: expandedRunner === runner.id ? undefined : 'none' }}>
                            <Collapse in={expandedRunner === runner.id}>
                              <Box p={2} className={classes.expandRow}>
                                <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                                  Recent Jobs
                                </Typography>
                                {!runnerJobs[runner.id] ? (
                                  <CircularProgress size={20} />
                                ) : runnerJobs[runner.id].length === 0 ? (
                                  <Typography
                                    variant="caption"
                                    color={runnerJobsError[runner.id] ? 'error' : 'textSecondary'}
                                  >
                                    {runnerJobsError[runner.id]
                                      ? `작업 이력을 가져오지 못했습니다: ${runnerJobsError[runner.id]}`
                                      : 'No recent jobs'}
                                  </Typography>
                                ) : (
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Job</TableCell>
                                        <TableCell>Project</TableCell>
                                        <TableCell>Branch</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>When</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {runnerJobs[runner.id].slice(0, 10).map(job => (
                                        <TableRow key={job.id}>
                                          <TableCell className={classes.mono}>{job.name}</TableCell>
                                          <TableCell>{job.project?.path_with_namespace || '-'}</TableCell>
                                          <TableCell className={classes.mono}>{job.ref}</TableCell>
                                          <TableCell>
                                            <Chip label={job.status} size="small" className={pipelineChipClass(job.status, classes)} />
                                          </TableCell>
                                          <TableCell>{timeAgo(job.created_at)}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                )}
                                <Box mt={1}>
                                  <Typography variant="caption" color="textSecondary">
                                    Platform: {runner.platform || '-'} | Arch: {runner.architecture || '-'}
                                  </Typography>
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderProjects = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <FormControl className={classes.filterControl} size="small" variant="outlined">
                <InputLabel>Group</InputLabel>
                <Select
                  value={groupFilter}
                  onChange={e => setGroupFilter(e.target.value as string)}
                  label="Group"
                >
                  <MenuItem value="all">All Groups</MenuItem>
                  {groups.map(g => (
                    <MenuItem key={g} value={g}>{g}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="textSecondary">
                {filteredProjects.length} projects
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Project</TableCell>
                    <TableCell>Latest Pipeline</TableCell>
                    <TableCell>Open MRs</TableCell>
                    <TableCell>Last Activity</TableCell>
                    <TableCell width={40} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProjects.map(proj => (
                    <TableRow key={proj.id} hover>
                      <TableCell>
                        <a
                          href={proj.web_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.tableLink}
                        >
                          {proj.path_with_namespace}
                        </a>
                      </TableCell>
                      <TableCell>
                        {proj._latestPipeline ? (
                          <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                            <Chip
                              label={proj._latestPipeline.status}
                              size="small"
                              className={pipelineChipClass(proj._latestPipeline.status, classes)}
                            />
                            <a
                              href={proj._latestPipeline.web_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={classes.tableLink}
                            >
                              #{proj._latestPipeline.iid || proj._latestPipeline.id}
                            </a>
                            <Typography variant="caption" className={classes.mono}>
                              {proj._latestPipeline.ref}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {proj._openMRCount && proj._openMRCount > 0 ? (
                          <Chip
                            icon={<MergeTypeIcon style={{ fontSize: 14 }} />}
                            label={proj._openMRCount}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        ) : (
                          <Typography variant="caption" color="textSecondary">0</Typography>
                        )}
                      </TableCell>
                      <TableCell>{timeAgo(proj.last_activity_at)}</TableCell>
                      <TableCell>
                        <Tooltip title="Open in GitLab">
                          <IconButton size="small" href={proj.web_url} target="_blank" rel="noopener noreferrer">
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <Content>
      <ContentHeader title="GitLab Dashboard">
        <Typography variant="caption" color="textSecondary" style={{ marginRight: 8 }}>
          Updated: {lastRefresh.toLocaleTimeString()}
        </Typography>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={loadData} disabled={loading} className={classes.refreshBtn}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Button
          variant="outlined"
          size="small"
          href={gitlabBaseUrl}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<OpenInNewIcon />}
          style={{ marginLeft: 8 }}
        >
          GitLab
        </Button>
        <Button
          variant="outlined"
          size="small"
          href={`${gitlabBaseUrl}/admin/runners`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ marginLeft: 8 }}
        >
          Runners (Admin)
        </Button>
      </ContentHeader>

      {error && (
        <Box mb={2}>
          <Card style={{ backgroundColor: '#fff3e0' }}>
            <CardContent>
              <Typography color="error">
                <WarningIcon style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />
                {error}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {notices.length > 0 && (
        <Box mb={2}>
          <Card style={{ backgroundColor: '#fff8e1' }} data-testid="gitlab-scope-notices">
            <CardContent>
              {notices.map((n, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Typography key={`${i}-${n}`} variant="body2">
                  <WarningIcon
                    style={{ fontSize: 16, verticalAlign: 'middle', color: '#ff9800', marginRight: 4 }}
                  />
                  {n}
                </Typography>
              ))}
            </CardContent>
          </Card>
        </Box>
      )}

      <Box mb={2}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary">
          <Tab label="Overview" />
          <Tab label={`Runners (${runners.length})`} />
          <Tab label={`Projects (${projects.length})`} />
        </Tabs>
      </Box>

      {loading && recentPipelines.length === 0 ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {tab === 0 && renderOverview()}
          {tab === 1 && renderRunners()}
          {tab === 2 && renderProjects()}
        </>
      )}
    </Content>
  );
};
